(function () {
  'use strict';

  if (window.ArticlePage && window.ArticlePage.initialised) {
    window.ArticlePage.init();
    return;
  }

  var COPIED_CLASS = 'is-copied';
  var COPIED_TIMEOUT = 2200;
  var BOUND_ATTR = 'data-ap-bound';

  /**
   * Copy a string to the clipboard.
   * Uses the async Clipboard API where available (needs HTTPS, which every
   * Shopify storefront has) and falls back to a hidden textarea + execCommand
   * for older browsers and non-secure preview contexts.
   *
   * @param {string} text
   * @returns {Promise<void>}
   */
  function copyToClipboard(text) {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text);
    }

    return new Promise(function (resolve, reject) {
      var textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);

      var selection = document.getSelection();
      var previousRange = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

      textarea.select();

      var succeeded = false;
      try {
        succeeded = document.execCommand('copy');
      } catch (error) {
        succeeded = false;
      }

      document.body.removeChild(textarea);

      if (previousRange && selection) {
        selection.removeAllRanges();
        selection.addRange(previousRange);
      }

      if (succeeded) {
        resolve();
      } else {
        reject(new Error('Copy command was rejected by the browser.'));
      }
    });
  }

  /**
   * Wire up every copy-link button that has not been bound yet.
   * @param {ParentNode} root
   */
  function bindCopyButtons(root) {
    var buttons = root.querySelectorAll('[data-copy-link]');

    Array.prototype.forEach.call(buttons, function (button) {
      if (button.hasAttribute(BOUND_ATTR)) return;
      button.setAttribute(BOUND_ATTR, 'true');

      var resetTimer = null;
      var defaultLabel = button.getAttribute('aria-label') || 'Copy link';

      button.addEventListener('click', function (event) {
        event.preventDefault();

        var url = button.getAttribute('data-copy-link') || window.location.href;

        copyToClipboard(url)
          .then(function () {
            button.classList.add(COPIED_CLASS);
            button.setAttribute('aria-label', 'Link copied');

            var toast = button.querySelector('.share__toast');
            if (toast) toast.textContent = 'Copied!';

            window.clearTimeout(resetTimer);
            resetTimer = window.setTimeout(function () {
              button.classList.remove(COPIED_CLASS);
              button.setAttribute('aria-label', defaultLabel);
            }, COPIED_TIMEOUT);
          })
          .catch(function () {
            window.prompt('Copy this link:', url);
          });
      });
    });
  }

  function bindShareLinks(root) {
    var links = root.querySelectorAll('[data-share-network]');

    Array.prototype.forEach.call(links, function (link) {
      if (link.hasAttribute(BOUND_ATTR)) return;
      link.setAttribute(BOUND_ATTR, 'true');

      link.addEventListener('click', function (event) {
        var href = link.getAttribute('href');
        if (!href || href === '#') return;

        var width = 600;
        var height = 540;
        var left = window.screenX + Math.max(0, (window.outerWidth - width) / 2);
        var top = window.screenY + Math.max(0, (window.outerHeight - height) / 2);

        var popup = window.open(
          href,
          'ap-share',
          'noopener,noreferrer,width=' + width + ',height=' + height + ',left=' + Math.round(left) + ',top=' + Math.round(top)
        );

        if (popup) {
          popup.focus();
          event.preventDefault();
        }
      });
    });
  }

  /**
   * Smooth-scroll in-page anchors that point at the enquiry form, and move
   * focus to the first field so keyboard users land in the right place.
   * @param {ParentNode} root
   */
  function bindAnchorScroll(root) {
    var links = root.querySelectorAll('[data-scroll-to]');

    Array.prototype.forEach.call(links, function (link) {
      if (link.hasAttribute(BOUND_ATTR)) return;
      link.setAttribute(BOUND_ATTR, 'true');

      link.addEventListener('click', function (event) {
        var targetId = link.getAttribute('data-scroll-to');
        var target = targetId ? document.getElementById(targetId) : null;
        if (!target) return;

        event.preventDefault();

        var prefersReducedMotion =
          window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        target.scrollIntoView({
          behavior: prefersReducedMotion ? 'auto' : 'smooth',
          block: 'start'
        });

        var firstField = target.querySelector('input, textarea');
        if (firstField) {
          window.setTimeout(function () {
            firstField.focus({ preventScroll: true });
          }, prefersReducedMotion ? 0 : 500);
        }
      });
    });
  }

  /* ------------------------------------------------------------------------
    Article-only sidebar search
  ---------------------------------------------------------------------- */

  var SEARCH_DEBOUNCE = 250;
  var SEARCH_MIN_CHARS = 1;

  /**
   * @param {ParentNode} root
   */
  function bindArticleSearch(root) {
    var widgets = root.querySelectorAll('[data-article-search]');

    Array.prototype.forEach.call(widgets, function (widget) {
      if (widget.hasAttribute(BOUND_ATTR)) return;
      widget.setAttribute(BOUND_ATTR, 'true');

      var input = widget.querySelector('[data-search-input]');
      var panel = widget.querySelector('[data-search-panel]');
      var empty = widget.querySelector('[data-search-empty]');
      var status = widget.querySelector('[data-search-status]');
      if (!input || !panel) return;

      var limit = parseInt(widget.getAttribute('data-limit'), 10) || 6;
      var notFoundLabel = widget.getAttribute('data-no-results') || 'Result not found for';

      var entries = [];
      Array.prototype.forEach.call(widget.querySelectorAll('[data-search-link]'), function (el) {
        entries.push({
          el: el,
          haystack: (el.getAttribute('data-search-haystack') || '').toLowerCase()
        });
      });

      var debounceTimer = null;
      var activeIndex = -1;
      var visible = [];

      function reveal(el) {
        var img = el.querySelector('img[data-src]');
        if (img) {
          img.src = img.getAttribute('data-src');
          img.removeAttribute('data-src');
        }
        el.hidden = false;
      }

      function openPanel() {
        panel.hidden = false;
        input.setAttribute('aria-expanded', 'true');
      }

      function resetRows() {
        for (var i = 0; i < entries.length; i++) {
          entries[i].el.hidden = true;
          entries[i].el.classList.remove('is-active');
          entries[i].el.setAttribute('aria-selected', 'false');
        }
        visible = [];
        activeIndex = -1;
        input.removeAttribute('aria-activedescendant');
      }

      function closePanel() {
        panel.hidden = true;
        if (empty) empty.hidden = true;
        resetRows();
        input.setAttribute('aria-expanded', 'false');
      }

      function setActive(index) {
        if (!visible.length) return;

        if (index < 0) index = visible.length - 1;
        if (index >= visible.length) index = 0;

        for (var i = 0; i < visible.length; i++) {
          var isActive = i === index;
          visible[i].classList.toggle('is-active', isActive);
          visible[i].setAttribute('aria-selected', isActive ? 'true' : 'false');
        }

        activeIndex = index;
        input.setAttribute('aria-activedescendant', visible[index].id);
        visible[index].scrollIntoView({ block: 'nearest' });
      }

      /**
       * Show the first `limit` anchors whose haystack contains the term.
       * @param {string} term
       */
      function filter(term) {
        var needle = term.toLowerCase();
        resetRows();

        for (var i = 0; i < entries.length; i++) {
          if (visible.length >= limit) break;
          var entry = entries[i];
          if (entry.haystack.indexOf(needle) === -1) continue;
          reveal(entry.el);
          visible.push(entry.el);
        }

        var message = notFoundLabel + ' ' + term;

        if (empty) {
          empty.textContent = message;
          empty.hidden = visible.length > 0;
        }

        if (status) {
          status.textContent = visible.length
            ? visible.length + ' article suggestion(s) available.'
            : message;
        }

        openPanel();
      }

      input.addEventListener('input', function () {
        var term = input.value.trim();
        window.clearTimeout(debounceTimer);

        if (term.length < SEARCH_MIN_CHARS) {
          closePanel();
          return;
        }

        debounceTimer = window.setTimeout(function () {
          filter(term);
        }, SEARCH_DEBOUNCE);
      });

      input.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') {
          closePanel();
          input.blur();
          return;
        }

        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          if (panel.hidden || !visible.length) return;
          event.preventDefault();
          setActive(event.key === 'ArrowDown' ? activeIndex + 1 : activeIndex - 1);
          return;
        }

        if (event.key === 'Enter') {
          if (panel.hidden || !visible.length) {
            if (!panel.hidden) event.preventDefault();
            return;
          }
          event.preventDefault();
          window.location.href = visible[activeIndex > -1 ? activeIndex : 0].href;
        }
      });

      input.addEventListener('focus', function () {
        if (input.value.trim().length >= SEARCH_MIN_CHARS) {
          filter(input.value.trim());
        }
      });

      document.addEventListener('click', function (event) {
        if (!widget.contains(event.target)) closePanel();
      });

      widget.addEventListener('focusout', function (event) {
        if (!widget.contains(event.relatedTarget)) closePanel();
      });
    });
  }

  /**
   * Bind every behaviour inside a root node (defaults to the document).
   * Safe to call repeatedly — each element is bound at most once.
   * @param {ParentNode} [root]
   */
  function init(root) {
    var scope = root || document;
    if (!scope || typeof scope.querySelectorAll !== 'function') return;

    bindCopyButtons(scope);
    bindShareLinks(scope);
    bindAnchorScroll(scope);
    bindArticleSearch(scope);
  }

  window.ArticlePage = {
    init: init,
    initialised: true
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target || document);
  });
})();
